// Thin client for analytics.asset.college/api/*. Same-origin as the SPA,
// so no auth token handling lives on the client — Cloudflare Zero Trust
// gates the whole hostname and any session cookies travel automatically.

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  // 204 / empty responses
  if (res.status === 204) return undefined as T;
  const ct = res.headers.get('Content-Type') ?? '';
  if (!ct.includes('application/json')) return undefined as T;
  return (await res.json()) as T;
}

export type HealthResponse = { ok: boolean; time: string };

export interface QuerySpec {
  measure: string;
  params?: Record<string, string | number | boolean | null>;
}

export interface QueryResult {
  measure: string;
  label: string;
  rows: Record<string, unknown>[];
}

export interface DefinitionSummary {
  definitionId: string;
  kind: 'measure' | 'dimension' | 'model';
  subject: string;
  label: string;
  description: string | null;
}

export type StaffStatus = 'active' | 'inactive' | 'departed';

export type StaffSource = 'axcelerate' | 'ghl' | 'ringcentral' | 'xero' | 'm365';

export interface StaffGroupRef {
  groupId: string;
  groupName: string;
}

export interface StaffLink {
  source: StaffSource;
  sourceIdentifier: string;
  linkedAt: string;
}

export interface Staff {
  staffId: string;
  displayName: string;
  givenName: string | null;
  familyName: string | null;
  primaryEmail: string | null;
  status: StaffStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  groups: StaffGroupRef[];
  links: StaffLink[];
}

export interface StaffGroup {
  groupId: string;
  groupName: string;
  description: string | null;
}

export type SyncRunStatus = 'success' | 'partial' | 'failed' | 'skipped' | null;

export interface AxcelerateSyncRun {
  runId: number;
  trigger: 'cron' | 'manual';
  startedAt: string;
  finishedAt: string | null;
  status: SyncRunStatus;
  domainsSynced: number | null;
  recordsSynced: number | null;
  error: string | null;
  rawObjectPrefix: string | null;
}

export interface AxcelerateDomain {
  domainId: number;
  domainName: string;
  isHeadOffice: boolean;
  isEnabled: boolean;
  lastSeenAt: string | null;
  notes: string | null;
}

export interface DataQualityIssue {
  issueId: number;
  source: string;
  issueType: string;
  sourceIdentifier: string | null;
  context: string | null;
  detectedAt: string;
  resolvedAt: string | null;
  resolutionNotes: string | null;
}

export interface StaffPayload {
  displayName: string;
  givenName?: string | null;
  familyName?: string | null;
  primaryEmail?: string | null;
  status: StaffStatus;
  notes?: string | null;
}

export type StaffPatch = Partial<StaffPayload>;

export const api = {
  health: () => request<HealthResponse>('/health'),

  query: (spec: QuerySpec) =>
    request<QueryResult>('/query', { method: 'POST', body: JSON.stringify(spec) }),

  definitions: () => request<{ definitions: DefinitionSummary[] }>('/definitions'),

  staff: {
    list: () => request<{ staff: Staff[] }>('/staff'),
    create: (payload: StaffPayload) =>
      request<{ staffId: string }>('/staff', { method: 'POST', body: JSON.stringify(payload) }),
    update: (id: string, payload: StaffPatch) =>
      request<{ ok: true }>(`/staff/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
    remove: (id: string) => request<{ ok: true }>(`/staff/${id}`, { method: 'DELETE' }),
    assignGroup: (id: string, groupId: string) =>
      request<{ ok: true }>(`/staff/${id}/groups`, {
        method: 'POST',
        body: JSON.stringify({ groupId }),
      }),
    unassignGroup: (id: string, groupId: string) =>
      request<{ ok: true }>(`/staff/${id}/groups/${groupId}`, { method: 'DELETE' }),
    addLink: (id: string, source: StaffSource, sourceIdentifier: string) =>
      request<{ ok: true }>(`/staff/${id}/links`, {
        method: 'POST',
        body: JSON.stringify({ source, sourceIdentifier }),
      }),
    removeLink: (id: string, source: StaffSource, sourceIdentifier: string) =>
      request<{ ok: true }>(
        `/staff/${id}/links/${source}/${encodeURIComponent(sourceIdentifier)}`,
        { method: 'DELETE' },
      ),
  },

  groups: {
    list: () => request<{ groups: StaffGroup[] }>('/groups'),
  },

  connectors: {
    axcelerate: {
      runs: () => request<{ runs: AxcelerateSyncRun[] }>('/connectors/axcelerate/runs'),
      domains: () => request<{ domains: AxcelerateDomain[] }>('/connectors/axcelerate/domains'),
      sync: () =>
        request<{
          status: string;
          domainsSynced: number;
          recordsSynced: number;
          error: string | null;
        }>('/connectors/axcelerate/sync', { method: 'POST' }),
    },
  },

  issues: {
    list: (includeResolved = false) =>
      request<{ issues: DataQualityIssue[] }>(
        includeResolved ? '/issues?resolved=true' : '/issues',
      ),
    resolve: (id: number, notes?: string) =>
      request<{ ok: true }>(`/issues/${id}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ notes: notes ?? null }),
      }),
  },
};
